import { SurveyAnswersState } from "@utils/redux/slices/surveyAnswers";
import { RecommendedPlanState } from "@utils/redux/slices/recommendedPlan";
import getStore from "@utils/redux/store";
import { initializeHandlers, firePixelEvent } from "@utils/pixels/handler";
import { getPromoCodeState } from "@utils/redux/slices/promoCode";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import { PurchaseProgramResponse } from "@utils/api/purchase/checkout";
import {
  shouldFireMedPixel,
  userHasPurchasedNoomClinical,
} from "@utils/userSegment/features";
import { MedProductType } from "@utils/constants";
import { getBMI, MED_SKU } from "@utils/medEligibility";
import { sendBeacon } from "@utils/fetch";
import uuid from "uuid";
import { BuyflowPurchaseIntentTriggeredProto } from "@noom/noom-contracts/noom_contracts/events/buyflow/marketing";
import { ScheduledBuyflowPurchaseTriggeredProto } from "@noom/noom-contracts/noom_contracts/events/scheduled/marketing/scheduled_buyflow_purchase_triggered";
import { GenderProto_Value } from "@noom/noom-contracts/noom_contracts/usermodel/baselined/demographics/gender";
import { convertNumberToMoney } from "@utils/noomContracts/moneyProto";
import {
  getCity,
  getCountryCode,
  getPostalCode,
  getRequestMetadata,
  getSubdivision,
} from "@utils/meristemContext";
import { allowCategories, LibCategories } from "@utils/consent";
import { getWeightInformation } from "@utils/calculateGoals";
import { captureException } from "@utils/error";
import { trackTask } from "@utils/monitoring/events/tasks";
import { UserDataState } from "@utils/redux/slices/userData";
import { shouldApplyLDUFlag } from "@utils/pixels/publishers/facebook";
import { getFacebookProperties } from "@utils/api/FacebookCapiApi";

export type PurchaseIntentDetails = {
  estimatedValue: number;
  medProductType: MedProductType;
  medPlanType: MED_SKU;
};

export function initConversionTracker() {
  initializeHandlers();
  firePixelEvent("init", {});
}

/**
 * Tracks the 'Lead' event.
 */
export function trackLead(email: string, surveyAnswers?: SurveyAnswersState) {
  const { userData } = getStore().getState();
  firePixelEvent("lead", { email, userData, surveyAnswers });
}

/**
 * Tracks the 'Purchase' event.
 */
export async function trackPurchase(
  email: string,
  plan: RecommendedPlanState,
  surveyAnswers: SurveyAnswersState,
  order: PurchaseProgramResponse
) {
  const { userData } = getStore().getState();
  const promoCode = getPromoCodeState();
  firePixelEvent("purchase", {
    email,
    plan,
    surveyAnswers,
    order,
    userData,
    promoCode,
  });

  if (allowCategories([LibCategories.targetingCookies])) {
    // Send ScheduledBuyflowPurchaseTriggeredProto event to marketing service
    sendScheduledBuyflowPurchaseTriggered(email, plan, surveyAnswers, userData);
  }
}

/**
 * Tracks an event that is corelated with a purchase decision
 * this is used in cases where we can't track the purchase itself
 */
export function trackPurchaseIntent(
  email: string,
  plan: RecommendedPlanState,
  surveyAnswers: SurveyAnswersState,
  purchaseIntentDetails: PurchaseIntentDetails
) {
  const { userData } = getStore().getState();
  const promoCode = getPromoCodeState();
  firePixelEvent("purchaseIntent", {
    email,
    plan,
    surveyAnswers,
    userData,
    estimatedValue: purchaseIntentDetails.estimatedValue,
    medProductType: purchaseIntentDetails.medProductType,
    medPlanType: purchaseIntentDetails.medPlanType,
    promoCode,
  });

  if (allowCategories([LibCategories.targetingCookies])) {
    // Send BuyflowPurchaseIntentTriggeredProto event to marketing service
    sendBuyflowPurchaseIntentTriggered(email, plan, surveyAnswers, userData);
  }
}

export function trackViewContent() {
  const { userData } = getStore().getState();
  firePixelEvent("viewContent", {
    value: 2.0,
    currency: "USD",
    userId: userData.user_id,
  });
}

// TODO(george): Add a better way to segment users that purchased med if we add post-signup purchases
// to the med flow
export function trackFastForwardTrial({
  discountOffered,
}: {
  discountOffered: boolean;
}) {
  const { userData } = getStore().getState();
  const surveyAnswers = getSurveyAnswers();
  if (shouldFireMedPixel()) {
    return Promise.resolve();
  }
  return firePixelEvent("trialFastForward", {
    discountOffered,
    email: userData.email || surveyAnswers.email || "",
    userData,
  });
}

export function trackNoomPremium(signupEltv13: number, products: string[]) {
  if (userHasPurchasedNoomClinical() || shouldFireMedPixel()) {
    return Promise.resolve();
  }
  const { userData } = getStore().getState();
  const surveyAnswers = getSurveyAnswers();
  return firePixelEvent("noomPremium", {
    signupEltv13,
    products,
    email: userData.email || surveyAnswers.email || "",
    userData,
  });
}

export function trackNoomSummit(signupEltv13: number, products: string[]) {
  if (shouldFireMedPixel()) {
    return Promise.resolve();
  }
  return firePixelEvent("noomSummit", {
    signupEltv13,
    products,
  });
}

export function trackLdsGoals(ldsHopingToAchieve: string[]) {
  const { userData } = getStore().getState();
  const surveyAnswers = getSurveyAnswers();

  return firePixelEvent("ldsGoals", {
    userData,
    email: userData.email || surveyAnswers.email || "",
    ldsHopingToAchieve,
  });
}

// Extract user age from survey answers
function getAgeFromSurvey(answers: SurveyAnswersState): number | undefined {
  if (answers.ageRange?.[0]) {
    const ageRange = answers.ageRange[0];
    const match = ageRange.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : undefined;
  }
  return undefined;
}

// Get gender from survey answers
function getGenderFromSurvey(
  answers: SurveyAnswersState
): GenderProto_Value | undefined {
  const gender = answers.gender?.[0];
  if (!gender) return undefined;

  // Map survey gender values to proto enum
  switch (gender.toLowerCase()) {
    case "male":
      return "MALE";
    case "female":
      return "FEMALE";
    case "nonbinary":
    case "selfidentify":
      return "NONBINARY";
    default:
      return "UNKNOWN";
  }
}

// Check if user has health risk
function getHealthRiskFromSurvey(answers: SurveyAnswersState): boolean {
  return Boolean(
    answers.currentHealthRiskMedical &&
      answers.currentHealthRiskMedical.length > 0 &&
      answers.currentHealthRiskMedical[0] !== "none"
  );
}

// Check if user has important date
function getImportantDateFromSurvey(answers: SurveyAnswersState): boolean {
  return Boolean(answers.importantDate && answers.importantDate[0] === "yes");
}

// Check if user took weight loss medications
function getWeightLossMedsFromSurvey(answers: SurveyAnswersState): boolean {
  return Boolean(
    answers.previousMedications &&
      ["yes", "takingWeightLossMed", "beenPrescribed"].includes(
        answers.previousMedications[0]
      )
  );
}

/**
 * Creates ScheduledBuyflowPurchaseTriggeredProto event object
 * This is separated for easier testing
 */
export function createScheduledBuyflowPurchaseEvent(
  email: string,
  plan: RecommendedPlanState,
  surveyAnswers: SurveyAnswersState,
  userData: UserDataState
): ScheduledBuyflowPurchaseTriggeredProto {
  const countryCode = getCountryCode();

  // Extract Facebook properties and user data using shared functions
  const { fbc, fbp } = getFacebookProperties();
  const { userAgent } = navigator;

  return {
    userAccessCode: userData.access_code || "",
    growthUserId: userData.user_id,
    email,
    age: getAgeFromSurvey(surveyAnswers),
    gender: getGenderFromSurvey(surveyAnswers),
    countryCode,
    state: getSubdivision(),
    currentBmi: getBMI(),
    hasHealthRisk: getHealthRiskFromSurvey(surveyAnswers),
    hasImportantDate: getImportantDateFromSurvey(surveyAnswers),
    tookWeightLossMeds: getWeightLossMedsFromSurvey(surveyAnswers),
    weightLossGoal: createWeightLossGoalData(surveyAnswers),
    optedOut: shouldApplyLDUFlag(),
    gdprConsent: surveyAnswers.gdprConsent,
    pendingDoubleOptIn: surveyAnswers.pendingDoubleOptIn,
    currentUrl: window.location.href,
    facebookBrowserId: fbp,
    facebookClickId: fbc,
    clientIpAddress: getRequestMetadata().userIp,
    clientUserAgent: userAgent,
    city: getCity(),
    firstName: surveyAnswers.firstName,
    zipcode: getPostalCode(),
    isClinicalRoutePurchase: shouldFireMedPixel(),
  };
}

/**
 * Creates BuyflowPurchaseIntentTriggeredProto event object
 * This is separated for easier testing
 */
export function createBuyflowPurchaseIntentEvent(
  email: string,
  plan: RecommendedPlanState,
  surveyAnswers: SurveyAnswersState,
  userData: UserDataState
): BuyflowPurchaseIntentTriggeredProto {
  const trialFeeMoney = convertNumberToMoney(
    plan.trial_fee_total_amount || plan.trial_fee,
    plan.currency
  );
  const planPriceMoney = convertNumberToMoney(plan.price, plan.currency);
  const countryCode = getCountryCode();

  // Extract Facebook properties and user data using shared functions
  const { fbc, fbp } = getFacebookProperties();
  const { userAgent } = navigator;

  return {
    userId: userData.user_id,
    email,
    age: getAgeFromSurvey(surveyAnswers),
    gender: getGenderFromSurvey(surveyAnswers),
    countryCode,
    state: getSubdivision(),
    currentBmi: getBMI(),
    hasHealthRisk: getHealthRiskFromSurvey(surveyAnswers),
    hasImportantDate: getImportantDateFromSurvey(surveyAnswers),
    tookWeightLossMeds: getWeightLossMedsFromSurvey(surveyAnswers),
    weightLossGoal: createWeightLossGoalData(surveyAnswers),
    planId: plan.braintree_id,
    planRecurringPrice: planPriceMoney,
    planBillingCycle: {
      count: plan.billing_cycle_duration,
      unit: plan.billing_cycle_duration_units,
    },
    trialPeriod: {
      count: plan.trial_duration,
      unit: "DAYS",
    },
    trialFee: trialFeeMoney,
    paidAtSignup: plan.has_trial ? trialFeeMoney : planPriceMoney,
    discount: convertNumberToMoney(
      getPromoCodeState().promoDiscountAmount || 0,
      plan.currency
    ),
    purchaseIntentDate: new Date().toISOString(),
    optedOut: shouldApplyLDUFlag(),
    gdprConsent: surveyAnswers.gdprConsent,
    pendingDoubleOptIn: surveyAnswers.pendingDoubleOptIn,
    currentUrl: window.location.href,
    facebookBrowserId: fbp,
    facebookClickId: fbc,
    clientIpAddress: getRequestMetadata().userIp,
    clientUserAgent: userAgent,
    city: getCity(),
    firstName: surveyAnswers.firstName,
    zipcode: getPostalCode(),
  };
}

/**
 * Sends BuyflowPurchaseIntentTriggeredProto event to marketing service
 * This is used to trigger eltv generation and send data to pixel APIs
 */
async function sendBuyflowPurchaseIntentTriggered(
  email: string,
  plan: RecommendedPlanState,
  surveyAnswers: SurveyAnswersState,
  userData: UserDataState
) {
  const task = trackTask("SendBuyflowPurchaseIntentEvent");
  try {
    task.start();
    const buyflowPurchaseIntentEvent = createBuyflowPurchaseIntentEvent(
      email,
      plan,
      surveyAnswers,
      userData
    );

    await sendBeacon("/pixel/v1/i/dataStreamEventTracking/", {
      sharedProperties: {
        eventTimestamp: new Date(),
        eventUuid: uuid.v4(),
      },
      buyflowPurchaseIntentTriggered: buyflowPurchaseIntentEvent,
    });
    task.success();
  } catch (err) {
    captureException(err);
    task.failed(err);
  }
}

/**
 * Sends ScheduledBuyflowPurchaseTriggeredProto event to marketing service
 * This is used to trigger scheduled eltv generation and send data to pixel APIs
 */
async function sendScheduledBuyflowPurchaseTriggered(
  email: string,
  plan: RecommendedPlanState,
  surveyAnswers: SurveyAnswersState,
  userData: UserDataState
) {
  const task = trackTask("SendScheduledBuyflowPurchaseEvent");
  try {
    task.start();
    const scheduledBuyflowPurchaseEvent = createScheduledBuyflowPurchaseEvent(
      email,
      plan,
      surveyAnswers,
      userData
    );
    const now = new Date();
    const startTime = new Date(now.getTime() + 15 * 60 * 1000); // 15 minutes from now
    const endTime = new Date(now.getTime() + 3 * 60 * 60 * 1000); // 3 hours from now

    await sendBeacon("/pixel/v1/i/dataStreamEventTracking/", {
      sharedProperties: {
        eventTimestamp: new Date(),
        eventUuid: uuid.v4(),
      },
      scheduledEvent: {
        sharedProperties: {
          desiredPublishWindowStartTime: startTime,
          desiredPublishWindowEndTime: endTime,
        },
        scheduledBuyflowPurchaseTriggered: scheduledBuyflowPurchaseEvent,
      },
    });
    task.success();
  } catch (err) {
    captureException(err);
    task.failed(err);
  }
}

// Creates weight loss goal data structure based on survey answers
function createWeightLossGoalData(surveyAnswers = getSurveyAnswers()) {
  const { weightValueKg, targetWeightKg } = getWeightInformation(surveyAnswers);

  if (!weightValueKg || !targetWeightKg) {
    return null;
  }

  const weightLossGoalData = {
    // Core weight data (required for calculation)
    startWeightInKg: weightValueKg,
    targetWeightInKg: targetWeightKg,
  };

  return weightLossGoalData;
}
